/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { groupBy, isEmpty, isUndefined, sortBy, sumBy, uniqBy } from 'lodash';
import { SecurityModel } from 'api';
import { CAP_TABLE_TERMS, SHARES_FULLY_DILUTED_ALIAS } from 'common/constants/cap-table';
import { OPTION, WARRANT } from 'common/constants/securityTypes';
import { RowsGroupsAction } from 'common/reducers/rowsGroups/types';
import { CapTableInfo } from 'common/types/capTable';
import { Fund } from 'common/types/fund';
import { FieldAttribute } from 'common/types/store';
import CellAnnotationValueViewer from 'components/CellAnnotationValueViewer';
import { buildCellKey } from 'components/CellAnnotationValueViewer/types';
import { TableTerms } from 'components/EmptyTableMessage/types';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import conditions from 'pages/CapTable/cap-table/utilities/conditions';
import { customValidations, reverseParser } from 'pages/CapTable/cap-table/utilities/index';
import parser from 'pages/CapTable/cap-table/utilities/parser';
import { FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG } from 'pages/CapTable/cell-annotations';
import { useCapTableDirtyCellStore } from 'store/useCapTableDirtyCellStore';
import afterCellChanged from './afterCellChanged';
import { aiMetadataKeyOf } from './aiFieldMap';
import { mergeSecuritiesWithLocal } from './mergeSecuritiesWithLocal';
import totalParser from './totalParser';
import { useCapTableRowGroups } from './useCapTableRowGroups';
import { CapTableCellWithReferences, CaptableColumn } from '../components/captable-table/CaptableTable.props';
import { rowConfig, securityTemplate } from '../data';

interface UseCaptableDataProps {
  captableInfo: CapTableInfo;
  measurementDate: string;
  fieldAttributes: FieldAttribute[];
  isDisabled: boolean;
  hasVariableLiquidationPreference: boolean;
  hasVariableDividendRate: boolean;
  fundList?: Fund[] | null;
  existingHiddenNoteColumns: SecurityModel[];
  firmName: string;
  captableCurrency?: string;
}

interface UseCapTableDataResponse {
  spreadsheet: SpreadsheetConfig | null | undefined;
  tableTerms: TableTerms;
  totalParser: typeof totalParser;
  visibleColumns: CaptableColumn[];
  setVisibleColumns: (visibleColumns: CaptableColumn[]) => void;
  virtualColumns: CaptableColumn[];
  setColumns: (columns: CaptableColumn[]) => void;
  columns: CaptableColumn[];
  isButtonDisabled: boolean;
  setIsButtonDisabled: (isButtonDisabled: boolean) => void;
  rowGroups: object;
  setRowGroups: Dispatch<RowsGroupsAction>;
  setCaptableSetProgrammaticUpdate: (isProgrammatic: boolean) => void;
}

const useCaptableData = ({
  captableInfo,
  fundList,
  existingHiddenNoteColumns,
  measurementDate,
  fieldAttributes,
  isDisabled,
  hasVariableLiquidationPreference,
  hasVariableDividendRate,
  firmName,
  captableCurrency,
}: UseCaptableDataProps): UseCapTableDataResponse => {
  const [columns, setColumns] = useState<CaptableColumn[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<CaptableColumn[] | undefined>([]);
  const [virtualColumns, setVirtualColumns] = useState<CaptableColumn[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>();
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig | null | undefined>(undefined);
  const captableSetProgrammaticUpdate = useRef(false);

  const setCaptableSetProgrammaticUpdate = useCallback((isProgrammatic: boolean) => {
    captableSetProgrammaticUpdate.current = isProgrammatic;
  }, []);

  const syncColumnsFromSecurities = useCallback(() => {
    if (!captableInfo?.securities) return;
    const incoming = captableInfo.securities as CaptableColumn[];
    const { dirtyBySecurityId } = useCapTableDirtyCellStore.getState();
    setColumns(prev => mergeSecuritiesWithLocal(incoming, prev, dirtyBySecurityId));
  }, [captableInfo?.securities]);

  useEffect(() => {
    syncColumnsFromSecurities();
  }, [captableInfo?.securities]);

  const createVirtualColumns = (cols: CaptableColumn[]): CaptableColumn[] => {
    const sortedColumns = sortBy(cols, 'created_at');
    // security_group_ref identifies the securities that derive from the same column
    const uniqueSecurityGroupRefs: Partial<SecurityModel>[] = uniqBy(
      sortedColumns
        .filter(s => s.security_group_ref && s.id && [WARRANT, OPTION].includes(s.security_type))
        .map(s => ({
          // security_ref in the virtual columns is the security_group_ref of its hidden securities
          security_ref: s.security_group_ref,
          security_group_ref: s.security_group_ref,
          // Build the name of the virtual column based on the security:
          // "$2 Common Warrant" --> "Common Warrant"
          name: s.name.substr(s.name.indexOf(' ') + 1),
          security_type: s.security_type,
          underlying_security_ref: s.underlying_security_ref,
          underlying_security: s.underlying_security,
          order: s.order,
        })),
      'security_group_ref'
    );

    const calculateStrikePrice = (hiddenCols: CaptableColumn[]) => {
      let sharesByStrikePrices = 0;
      let totalShares = 0;

      hiddenCols.forEach(({ shares_outstanding, strike_price }) => {
        // Get the total of shares
        totalShares += Number(shares_outstanding);
        // Multiply each strike price by the number of associated shares
        sharesByStrikePrices += Number(shares_outstanding) * Number(strike_price);
      });
      return sharesByStrikePrices / totalShares;
    };

    if (uniqueSecurityGroupRefs.length > 0) {
      return uniqueSecurityGroupRefs.map(groupRef => {
        const securitiesToGroup = groupBy(sortedColumns, 'security_group_ref')[groupRef.security_group_ref!];
        const isUsedInFod = securitiesToGroup.some(security => security.fod_usage_details?.is_used_in_fod);
        const isUsedInBacksolve = securitiesToGroup.some(
          security => security.backsolve_usage_details?.is_used_in_backsolve
        );
        const isSoleBasketMember = securitiesToGroup.some(
          security => security.backsolve_usage_details?.is_sole_basket_member
        );
        return {
          ...securityTemplate,
          id: null, // this identifies a virtual column
          security_ref: groupRef.security_ref,
          cap_table: captableInfo?.id,
          name: groupRef.name!,
          shares_outstanding: sumBy(securitiesToGroup, security => Number(security.shares_outstanding)),
          security_type: groupRef.security_type!,
          underlying_security: groupRef.underlying_security,
          underlying_security_ref: groupRef.underlying_security_ref,
          shares_diluted: sumBy(securitiesToGroup, security => Number(security.shares_diluted)),
          strike_price: calculateStrikePrice(securitiesToGroup),
          options_ledger: securitiesToGroup.map(col => ({
            shares: col.shares_outstanding,
            price: col.strike_price,
            security_ref: col.security_ref,
          })),
          order: groupRef.order,
          security_group_ref: groupRef.security_group_ref,
          fod_usage_details: {
            is_used_in_fod: isUsedInFod,
            location: securitiesToGroup.flatMap(security => security.fod_usage_details?.location ?? []),
          },
          backsolve_usage_details: {
            is_used_in_backsolve: isUsedInBacksolve,
            is_sole_basket_member: isSoleBasketMember,
            location: securitiesToGroup.flatMap(security => security.backsolve_usage_details?.location ?? []),
          },
        } as unknown as CaptableColumn;
      });
    }
    return [];
  };

  useEffect(() => {
    if (columns?.length) {
      setVirtualColumns(createVirtualColumns(columns));
    } else {
      setVirtualColumns([]);
    }
  }, [columns]);

  // Update the visible columns when a column is deleted or added
  useEffect(() => {
    if (columns && measurementDate) {
      const tmpVisibleColumns = columns.filter(column => !column.is_deleted && !column.is_hidden);
      const visibleVirtualColumnRefs = tmpVisibleColumns
        .filter(({ isVirtualColumn }) => isVirtualColumn)
        .map(({ security_ref }) => security_ref);

      if (virtualColumns && !isEmpty(virtualColumns)) {
        virtualColumns.forEach(vc => {
          // Insert virtual columns
          if (!visibleVirtualColumnRefs.includes(vc.security_ref)) {
            // eslint-disable-next-line no-param-reassign
            vc.isVirtualColumn = true;
            tmpVisibleColumns.push(vc);
          }
        });
      }
      setVisibleColumns([...tmpVisibleColumns]);
    } else {
      setVisibleColumns(undefined);
    }
  }, [columns, virtualColumns, measurementDate, hasVariableLiquidationPreference]);

  const fundOwnershipDetails = useMemo(
    () => captableInfo?.fund_ownership?.fund_ownership_detail,
    [captableInfo?.fund_ownership]
  );

  const config = useMemo(
    () => rowConfig({ measurementDate, isDisabled, fundOwnershipDetails, fundList, firmName }),
    [measurementDate, isDisabled, fundOwnershipDetails, fundList]
  );

  // Use custom hook for row groups persistence
  const capTableId = captableInfo?.id;
  const { rowGroups, setRowGroups } = useCapTableRowGroups(capTableId);

  const handleCellChanged = useCallback((initialChanges: any, cells: any, rowConfigParam: any, tableDataParam: any) => {
    // Skip clearing logic if this is a programmatic update
    if (!captableSetProgrammaticUpdate.current) {
      initialChanges.forEach((change: { cell: CapTableCellWithReferences; value: any }) => {
        if (!change.cell) {
          return;
        }
        // Unsaved new columns have a falsy columnId and are intentionally skipped:
        // the merge preserves unsaved local columns wholesale. SCAL-8959.
        const { columnId, alias } = change.cell;
        if (columnId && alias) {
          // ICell widens columnId to string | number, but cap-table cells always
          // carry the numeric security id (parser sets columnId: column.id).
          useCapTableDirtyCellStore.getState().recordDirty(columnId as number, alias, change.value);
        }
        const shouldRemoveMappingRefs =
          ((change.cell as CapTableCellWithReferences).mappingReferences?.length || 0) > 0
          && +change.value !== +change.cell.value
          && +change.cell.value !== 0;
        const shouldRemoveAiMetadata = Boolean(change.cell.aiFieldMetadata);

        if (shouldRemoveMappingRefs || shouldRemoveAiMetadata) {
          // A manual edit strips the mapping/AI decoration, but must never
          // leave a select cell without its base viewer: SelectValueViewer is
          // the only component mapping a stored ref to its display name (e.g.
          // Underlying Security), and nothing downstream restores it. Fall
          // back to the row's base viewer (null when the row defines none).
          change.cell.className = '';
          change.cell.valueViewer = rowConfigParam?.find((row: any) => row.alias === alias)?.valueViewer ?? null;
        }

        if (shouldRemoveMappingRefs) {
          // Clear the cell's mapping references (only on manual edits)
          change.cell.mappingReferences = [];
          change.cell.columnarReferences = [];
          change.cell.columnarReferencesMetadata = [];
          change.cell.dispatchEvent?.();
        }

        if (shouldRemoveAiMetadata) {
          change.cell.aiFieldMetadata = null;
          change.cell.citationReferences = [];
          if (columnId && alias) {
            const fieldMetadata = tableDataParam?.columns?.find((c: any) => c.id === columnId)?.ai_finding
              ?.field_metadata;
            if (fieldMetadata) {
              // Findings are keyed by the canonical model field name (the
              // parser attaches them via aiFieldMap); legacy rows may also
              // carry a raw alias twin - purge both spellings.
              delete fieldMetadata[aiMetadataKeyOf(alias)];
              delete fieldMetadata[alias];
            }
          }
        }
      });
    }

    return afterCellChanged(
      initialChanges,
      cells,
      rowConfigParam,
      tableDataParam,
      captableSetProgrammaticUpdate.current
    );
  }, []);

  useEffect(() => {
    if (captableInfo?.securities?.length) {
      syncColumnsFromSecurities();
      setIsButtonDisabled(false);
    }
  }, [captableInfo]);

  const totalParserWithCellAnnotation = useMemo(() => {
    const captableId = captableInfo?.id;
    if (captableId == null) {
      return totalParser;
    }

    // The annotation_type segment ('cross_reference') matches the wire shape
    // returned by the backend for this cell - see useHydrateCaptableCellAnnotations.
    const annotationCellKey = buildCellKey('captable', captableId, 'fully_diluted_total', 'cross_reference');

    return (args: Parameters<typeof totalParser>[0]) => {
      const totals = totalParser(args);
      Object.values(totals).forEach(cell => {
        if (cell.alias === SHARES_FULLY_DILUTED_ALIAS) {
          cell.valueViewer = CellAnnotationValueViewer;
          cell.cellAnnotation = FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG;
          cell.cellAnnotationCellKey = annotationCellKey;
        }
      });
      return totals;
    };
  }, [captableInfo?.id]);

  useEffect(() => {
    if (!isUndefined(visibleColumns) && existingHiddenNoteColumns) {
      setSpreadsheet(
        new SpreadsheetConfig({
          columns: visibleColumns || [],
          parser,
          totalParser: totalParserWithCellAnnotation,
          rowConfig: config,
          conditions: conditions as any,
          name: 'captable',
          tableData: {
            columns,
            ...captableInfo,
            has_liquidation_preference_ledger: hasVariableLiquidationPreference,
            has_dividend_rate_ledger: hasVariableDividendRate,
            measurementDate,
            captableCurrency,
            isDisabled,
            existingHiddenNoteColumns,
          },
          // any as the constructor types these as `undefined`...
          reverseParser: reverseParser as any,
          afterCellChanged: handleCellChanged as any,
          customValidations: customValidations as any,
          showTotalColumn: true,
          showToolbar: true,
          tableTerms: CAP_TABLE_TERMS as any,
          allowConfirmAndDeleteColumn: !isDisabled,
          customDeleteConfirmation: !isDisabled,
          hasColTitle: true,
          currencyFormatter: false,
          allowEmptyValues: false,
          allowReorderColumns: !isDisabled,
          fieldAttributes,
          allowDeleteColumn: false,
          allowCopyRows: !isDisabled,
          allowCloneColumn: !isDisabled as any,
          allowAddMultipleColumns: !isDisabled,
          displayRowIndicator: true,
          displayColumnIndicator: true,
          // added during ts migration
          format: undefined,
          unitsFormatter: undefined,
        })
      );
    } else {
      setSpreadsheet(null);
    }
  }, [
    visibleColumns,
    isDisabled,
    hasVariableLiquidationPreference,
    hasVariableDividendRate,
    existingHiddenNoteColumns,
    totalParserWithCellAnnotation,
    captableCurrency,
    measurementDate,
  ]);

  return {
    spreadsheet,
    rowGroups,
    setRowGroups,
    tableTerms: CAP_TABLE_TERMS,
    totalParser,
    visibleColumns: visibleColumns ?? [],
    setVisibleColumns,
    virtualColumns,
    setColumns,
    columns,
    isButtonDisabled: isButtonDisabled ?? false,
    setIsButtonDisabled,
    setCaptableSetProgrammaticUpdate,
  };
};

export default useCaptableData;
